import { useQuery } from "@tanstack/react-query";
import { getAdminSettings } from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";
import { Button } from "@/components/ui/Button";
import { AuditCard, AuthProvidersCard, EmailCard } from "./system-cards";

/**
 * Super-admin system settings (multi-club).
 *
 * Platform-wide infrastructure that is shared across every club — email /
 * Brevo delivery, Google/Apple OAuth provider credentials, and the platform
 * settings audit trail. These are super-admin only (the backend endpoints
 * are gated by require_super_admin, apex/admin host only), distinct from a
 * club's own branding/flags which a club admin edits at /admin/settings
 * (→ /api/club/settings).
 *
 * The cards live in ./system-cards (they were cut from the club admin
 * console when these endpoints became super-admin only).
 */
export function SystemSettingsPage() {
  // Same queryKey the club SettingsPage uses for this payload so the two
  // never hold divergent copies while both exist during the transition.
  const settingsQuery = useQuery({
    queryKey: unscopedKey("admin-settings"),
    queryFn: getAdminSettings,
  });

  return (
    <div data-testid="super-admin-system" className="flex flex-col gap-4">
      <AdminPageHeader
        eyebrow="System"
        title="System settings"
        description="Shared email and sign-in infrastructure. Club branding stays with each club."
      />
      <AuthProvidersCard />
      <EmailCard />
      {settingsQuery.isError ? (
        <div role="alert" className="platform-panel">
          <p className="text-destructive-text">
            Could not load the settings audit.
          </p>
          <Button variant="ghost" onClick={() => void settingsQuery.refetch()}>
            Retry
          </Button>
        </div>
      ) : settingsQuery.isPending ? (
        <p role="status">Loading settings audit…</p>
      ) : (
        <AuditCard audit={settingsQuery.data?.audit ?? []} />
      )}
    </div>
  );
}
